/**
 * CARB EMFAC Fleet DB composition helpers.
 * Primary table: emfac2025_fleet_mat (city/county/state rollups from FleetDB + county EMFAC).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export type FleetGeographyType = 'City' | 'County' | 'State';

export type FleetStratumRow = {
  year: number;
  vehicle_category: string;
  class: string;
  fuel_type: string;
  vehicle_population: number;
  gco2_per_mile: number | null;
  total_vmt: number | null;
  vmt_per_veh: number | null;
};

export type YearValue = { year: number; value: number };

export type NamedShare = { key: string; label: string; value: number; sharePct: number };

export type FuelYearStack = {
  year: number;
  byFuel: Record<string, number>;
  total: number;
};

export type FleetCompositionBundle = {
  geographyType: FleetGeographyType;
  geofips: number;
  locationLabel: string;
  rows: FleetStratumRow[];
  californiaRows: FleetStratumRow[];
  years: number[];
  ldvIntensityLocation: YearValue[];
  ldvIntensityCalifornia: YearValue[];
  metrics: FleetCompositionMetrics | null;
};

export type FleetCompositionMetrics = {
  latestYear: number;
  baselineYear: number;
  ldvGPerMileLatest: number;
  ldvGPerMileBaseline: number;
  ldvGPerMilePctChange: number;
  caLdvGPerMileLatest: number | null;
  totalVehiclesLatest: number;
  ldvSharePctLatest: number;
  zeroEmissionSharePctLatest: number;
  /** BEV + PHEV share of LDV registrations (latest year). */
  pluginSharePctLatest: number;
};

const FLEET_TABLES = ['emfac2025_fleet_mat', 'emfac2025_fleet', 'emfac2021_fleet_mat'] as const;

export const VEHICLE_CATEGORY_LABELS: Record<string, string> = {
  P: 'Passenger cars',
  T1: 'Light trucks (T1)',
  T2: 'Light trucks (T2)',
  T3: 'Medium trucks (T3)',
  T4: 'Medium trucks (T4)',
  T5: 'Heavy trucks (T5)',
  T6: 'Heavy trucks (T6)',
  T7: 'Heavy trucks (T7)',
  MC: 'Motorcycles',
  MH: 'Motor homes',
  BS: 'School buses',
  BT: 'Transit buses',
  B: 'Other buses',
};

export const FUEL_LABELS: Record<string, string> = {
  Gasoline: 'Gasoline',
  Diesel: 'Diesel',
  Electric: 'Battery electric',
  PluginHybrid: 'Plug-in hybrid',
  'Natural Gas': 'Natural gas',
  Hydrogen: 'Hydrogen',
};

export const FUEL_COLORS: Record<string, string> = {
  Gasoline: '#ef4444',
  Diesel: '#b45309',
  Electric: '#10b981',
  PluginHybrid: '#34d399',
  'Natural Gas': '#6366f1',
  Hydrogen: '#06b6d4',
};

export const CLASS_COLORS: Record<string, string> = {
  LDV: '#3b82f6',
  HDV: '#f59e0b',
};

const CATEGORY_COLOR_PALETTE = [
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0891b2',
  '#4f46e5',
  '#be123c',
  '#065f46',
  '#57534e',
  '#0f766e',
  '#9a3412',
];

export function vehicleCategoryLabel(code: string): string {
  return VEHICLE_CATEGORY_LABELS[code] || code;
}

export function fuelLabel(fuel: string): string {
  return FUEL_LABELS[fuel] || fuel;
}

export function categoryColor(code: string, index: number): string {
  return CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length];
}

function geographyTypeForLocation(location: string): FleetGeographyType {
  const t = supabaseLocationService.getLocationType(location);
  if (t === 'county') return 'County';
  if (t === 'state') return 'State';
  return 'City';
}

async function fetchFleetRowsForPlace(
  client: SupabaseClient,
  geofips: number,
  geographyType: FleetGeographyType,
  yearMin = 2015,
  yearMax = 2023
): Promise<FleetStratumRow[]> {
  const selectCols =
    'year, vehicle_category, class, fuel_type, vehicle_population, gco2_per_mile, total_vmt, vmt_per_veh';

  for (const table of FLEET_TABLES) {
    const all: FleetStratumRow[] = [];
    let from = 0;
    const pageSize = 1000;
    let usedTable = false;
    while (true) {
      const { data, error } = await client
        .from(table)
        .select(selectCols)
        .eq('geofips', geofips)
        .eq('geography_type', geographyType)
        .gte('year', yearMin)
        .lte('year', yearMax)
        .order('year', { ascending: true })
        .range(from, from + pageSize - 1);

      if (error) {
        if (!usedTable && all.length === 0) break; // try next table
        throw error;
      }
      usedTable = true;
      const batch = (data || []).map((r: any) => ({
        year: Number(r.year),
        vehicle_category: String(r.vehicle_category || ''),
        class: String(r.class || ''),
        fuel_type: String(r.fuel_type || ''),
        vehicle_population: Number(r.vehicle_population) || 0,
        gco2_per_mile: r.gco2_per_mile == null ? null : Number(r.gco2_per_mile),
        total_vmt: r.total_vmt == null ? null : Number(r.total_vmt),
        vmt_per_veh: r.vmt_per_veh == null ? null : Number(r.vmt_per_veh),
      }));
      all.push(...batch);
      if (batch.length < pageSize) break;
      from += pageSize;
    }
    if (all.length > 0) return all;
  }
  return [];
}

/** VMT-weighted mean gCO₂e/mile for LDV strata. */
export function vmtWeightedLdvGPerMile(rows: FleetStratumRow[]): YearValue[] {
  const byYear = new Map<number, { vmt: number; gramMiles: number }>();
  for (const r of rows) {
    if (r.class !== 'LDV') continue;
    const pop = r.vehicle_population || 0;
    let vmt = r.total_vmt;
    if (vmt == null || !Number.isFinite(vmt) || vmt <= 0) {
      if (r.vmt_per_veh != null && pop > 0) vmt = r.vmt_per_veh * pop;
      else continue;
    }
    const gpm = r.gco2_per_mile;
    if (gpm == null || !Number.isFinite(gpm) || gpm < 0) continue;
    const cur = byYear.get(r.year) || { vmt: 0, gramMiles: 0 };
    cur.vmt += vmt;
    cur.gramMiles += gpm * vmt;
    byYear.set(r.year, cur);
  }
  return Array.from(byYear.entries())
    .map(([year, s]) => ({ year, value: s.vmt > 0 ? s.gramMiles / s.vmt : 0 }))
    .filter((d) => d.value > 0)
    .sort((a, b) => a.year - b.year);
}

export function sharesForYear(
  rows: FleetStratumRow[],
  year: number,
  groupBy: 'class' | 'vehicle_category' | 'fuel_type',
  opts?: { classFilter?: 'LDV' | 'HDV' | null }
): NamedShare[] {
  const totals = new Map<string, number>();
  for (const r of rows) {
    if (r.year !== year) continue;
    if (opts?.classFilter && r.class !== opts.classFilter) continue;
    const key =
      groupBy === 'class' ? r.class : groupBy === 'vehicle_category' ? r.vehicle_category : r.fuel_type;
    if (!key) continue;
    totals.set(key, (totals.get(key) || 0) + (r.vehicle_population || 0));
  }
  const total = Array.from(totals.values()).reduce((a, b) => a + b, 0);
  if (total <= 0) return [];

  const labelFn =
    groupBy === 'vehicle_category'
      ? vehicleCategoryLabel
      : groupBy === 'fuel_type'
        ? fuelLabel
        : (k: string) => (k === 'LDV' ? 'Light-duty (LDV)' : k === 'HDV' ? 'Heavy-duty (HDV)' : k);

  return Array.from(totals.entries())
    .map(([key, value]) => ({
      key,
      label: labelFn(key),
      value,
      sharePct: (value / total) * 100,
    }))
    .sort((a, b) => b.value - a.value);
}

export function fuelStacksOverTime(
  rows: FleetStratumRow[],
  opts?: { classFilter?: 'LDV' | 'HDV' | null }
): FuelYearStack[] {
  const byYear = new Map<number, Record<string, number>>();
  for (const r of rows) {
    if (opts?.classFilter && r.class !== opts.classFilter) continue;
    const fuels = byYear.get(r.year) || {};
    const f = r.fuel_type || 'Other';
    fuels[f] = (fuels[f] || 0) + (r.vehicle_population || 0);
    byYear.set(r.year, fuels);
  }
  return Array.from(byYear.entries())
    .map(([year, byFuel]) => ({
      year,
      byFuel,
      total: Object.values(byFuel).reduce((a, b) => a + b, 0),
    }))
    .sort((a, b) => a.year - b.year);
}

export type GroupYearStack = {
  year: number;
  byKey: Record<string, number>;
  total: number;
};

/** Stacked series by vehicle_category or LDV/HDV class over time. */
export function classStacksOverTime(
  rows: FleetStratumRow[],
  groupBy: 'vehicle_category' | 'class'
): GroupYearStack[] {
  const byYear = new Map<number, Record<string, number>>();
  for (const r of rows) {
    const key = groupBy === 'class' ? r.class : r.vehicle_category;
    if (!key) continue;
    const groups = byYear.get(r.year) || {};
    groups[key] = (groups[key] || 0) + (r.vehicle_population || 0);
    byYear.set(r.year, groups);
  }
  return Array.from(byYear.entries())
    .map(([year, byKey]) => ({
      year,
      byKey,
      total: Object.values(byKey).reduce((a, b) => a + b, 0),
    }))
    .sort((a, b) => a.year - b.year);
}

function buildMetrics(
  locIntensity: YearValue[],
  caIntensity: YearValue[],
  rows: FleetStratumRow[]
): FleetCompositionMetrics | null {
  if (!locIntensity.length) return null;
  const latest = locIntensity[locIntensity.length - 1];
  const baseline = locIntensity[0];
  const caLatest = caIntensity.find((d) => d.year === latest.year) || caIntensity[caIntensity.length - 1];

  const latestRows = rows.filter((r) => r.year === latest.year);
  const totalVehicles = latestRows.reduce((s, r) => s + (r.vehicle_population || 0), 0);
  const ldvPop = latestRows
    .filter((r) => r.class === 'LDV')
    .reduce((s, r) => s + (r.vehicle_population || 0), 0);
  const pluginPop = latestRows
    .filter(
      (r) => r.class === 'LDV' && (r.fuel_type === 'Electric' || r.fuel_type === 'PluginHybrid')
    )
    .reduce((s, r) => s + (r.vehicle_population || 0), 0);
  const zePop = latestRows
    .filter(
      (r) =>
        r.class === 'LDV' &&
        (r.fuel_type === 'Electric' || r.fuel_type === 'Hydrogen')
    )
    .reduce((s, r) => s + (r.vehicle_population || 0), 0);

  const pctChange =
    baseline.value > 0 ? ((latest.value - baseline.value) / baseline.value) * 100 : 0;

  return {
    latestYear: latest.year,
    baselineYear: baseline.year,
    ldvGPerMileLatest: latest.value,
    ldvGPerMileBaseline: baseline.value,
    ldvGPerMilePctChange: pctChange,
    caLdvGPerMileLatest: caLatest?.value ?? null,
    totalVehiclesLatest: totalVehicles,
    ldvSharePctLatest: totalVehicles > 0 ? (ldvPop / totalVehicles) * 100 : 0,
    zeroEmissionSharePctLatest: ldvPop > 0 ? (zePop / ldvPop) * 100 : 0,
    pluginSharePctLatest: ldvPop > 0 ? (pluginPop / ldvPop) * 100 : 0,
  };
}

export async function fetchFleetCompositionBundle(
  location: string,
  geofips: number | null | undefined,
  client: SupabaseClient = supabaseClient
): Promise<FleetCompositionBundle | null> {
  if (geofips == null || !Number.isFinite(Number(geofips))) return null;

  await supabaseLocationService.initialize().catch(() => {});
  const geographyType = geographyTypeForLocation(location);
  const gf = Number(geofips);

  const [rows, californiaRows] = await Promise.all([
    fetchFleetRowsForPlace(client, gf, geographyType),
    fetchFleetRowsForPlace(client, 6, 'State'),
  ]);

  if (!rows.length) return null;

  const ldvIntensityLocation = vmtWeightedLdvGPerMile(rows);
  const ldvIntensityCalifornia = vmtWeightedLdvGPerMile(californiaRows);
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);

  return {
    geographyType,
    geofips: gf,
    locationLabel: location,
    rows,
    californiaRows,
    years,
    ldvIntensityLocation,
    ldvIntensityCalifornia,
    metrics: buildMetrics(ldvIntensityLocation, ldvIntensityCalifornia, rows),
  };
}
